import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { ChevronRight, Info, Link2, MoreHorizontal, Plus, Settings, Trash2 } from 'lucide-react'
import { ProjectDetailsDrawer } from './SpaceDialogs'
import { LocationSwatch } from './SpaceSwatch'
import { DropLine, RowCount, rowClass, sidebarDragPreview } from './SidebarRows'
import { canReorderProject, resolveProjectDrop, rowZone } from './sidebarDnd'
import { useDeleteProject } from './useDeleteProject'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectDropPosition, ProjectReorderEvent } from '@/routes/features/projects/hooks/useProjectReorder'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { cn } from '@/lib/utils'

const extractProjectDragData = createDragExtractor('project')
const extractPageDragData = createDragExtractor('page')

interface SpaceProjectRowProps {
  project: ProjectDocument
  spaceKey: string
  workspaceSlug: string
  isActive: boolean
  openCount: number
  /** The space's projects in sidebar order; projects only reorder among these. */
  orderedIds: ReadonlyArray<string>
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: ProjectReorderEvent) => void
  dropIndicator: { id: string; position: ProjectDropPosition } | null
  onDropIndicatorChange: (indicator: { id: string; position: ProjectDropPosition } | null) => void
  /** Whether a dragged doc can move into this project. */
  canAcceptDoc: (docId: string) => boolean
  /** A doc was dropped on the row: move it (and its sub-pages) here. */
  onDropDoc: (docId: string) => void
  onNewTask: () => void
  /**
   * Set only while you're in one of this project's docs (design 32b): the row
   * shows its docs underneath, and the chevron folds them away.
   */
  docsExpanded?: boolean
  onToggleDocs?: () => void
}

/**
 * A project under a drilled-in space (design 22c): its badge, name and one
 * number, its open tasks. Its Tasks / Docs / Views / Cycles are tabs in the
 * project's top bar, not tree rows (design 21 C). The whole row drags to
 * reorder; dropping a doc on it moves the doc into the project. On hover,
 * "+" (new task) and "⋯" replace the count; ⋯ also opens on right-click.
 * Like doc rows it keeps a 14px chevron slot, used only while the project's
 * docs are shown because you're reading one of them (design 32c).
 */
export function SpaceProjectRow({
  project,
  spaceKey,
  workspaceSlug,
  isActive,
  openCount,
  orderedIds,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
  canAcceptDoc,
  onDropDoc,
  onNewTask,
  docsExpanded,
  onToggleDocs,
}: SpaceProjectRowProps) {
  const { t } = useTranslation('projects')
  const { t: tCommon } = useTranslation('common')
  const { t: tNav } = useTranslation('navigation')
  const navigate = useNavigate()
  const [showDetails, setShowDetails] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [isDocOver, setIsDocOver] = useState(false)
  const rowRef = useRef<HTMLDivElement>(null)
  const pk = project.projectKey.toLowerCase()
  const projectPath = `/${workspaceSlug}/spaces/${spaceKey}/projects/${pk}`
  const { openTaskCount, isLoading: deletePlanLoading, deleteProjectTree } = useDeleteProject(project.id, showDeleteConfirm)

  const isDragging = draggingId === project.id
  const indicator = dropIndicator?.id === project.id ? dropIndicator.position : null

  useEffect(() => {
    const element = rowRef.current
    if (!element) return
    const projectIds = new Set(orderedIds)
    const positionFor = (sourceId: string, clientY: number) =>
      resolveProjectDrop(orderedIds, sourceId, project.id, rowZone(clientY, element.getBoundingClientRect()).edge)
    return combine(
      draggable({
        element,
        getInitialData: () => ({ type: 'project', id: project.id }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => sidebarDragPreview(nativeSetDragImage, element, project.name),
        onDragStart: () => {
          onDragStart(project.id)
          onDropIndicatorChange(null)
        },
        onDrop: () => {
          onDragEnd()
          onDropIndicatorChange(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const projectData = extractProjectDragData(source.data)
          if (projectData) return canReorderProject(projectData.id, project.id, projectIds)
          const pageData = extractPageDragData(source.data)
          return pageData !== null && canAcceptDoc(pageData.id)
        },
        getData: () => ({ type: 'project', id: project.id }),
        onDrag: ({ location, source }) => {
          const projectData = extractProjectDragData(source.data)
          if (projectData) {
            const position = positionFor(projectData.id, location.current.input.clientY)
            onDropIndicatorChange(position ? { id: project.id, position } : null)
            return
          }
          setIsDocOver(true)
        },
        onDragLeave: () => {
          setIsDocOver(false)
          onDropIndicatorChange(null)
        },
        onDrop: ({ location, source }) => {
          setIsDocOver(false)
          onDropIndicatorChange(null)
          const projectData = extractProjectDragData(source.data)
          if (projectData) {
            const position = positionFor(projectData.id, location.current.input.clientY)
            if (position) onReorder({ sourceId: projectData.id, targetId: project.id, dropPosition: position })
            return
          }
          const pageData = extractPageDragData(source.data)
          if (pageData) onDropDoc(pageData.id)
        },
      }),
    )
  }, [project.id, project.name, orderedIds, onDragStart, onDragEnd, onReorder, onDropIndicatorChange, canAcceptDoc, onDropDoc])

  const open = () =>
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
      params: { workspaceSlug, spaceKey, projectKey: pk },
    })

  return (
    <>
      <div
        ref={rowRef}
        data-testid="sidebar-project-row"
        className={rowClass(
          isActive,
          cn(
            'cursor-grab pl-1 active:cursor-grabbing',
            // A dashed slot stays where the row was while it's lifted.
            isDragging && 'border border-dashed border-border bg-transparent shadow-none hover:bg-transparent [&>*]:invisible',
            isDocOver && 'bg-brand-tint text-foreground ring-1 ring-brand hover:bg-brand-tint',
          ),
        )}
        onClick={open}
        onContextMenu={(e) => {
          e.preventDefault()
          setMenuOpen(true)
        }}
      >
        {indicator && <DropLine edge={indicator === 'before' ? 'top' : 'bottom'} indentPx={4} />}
        <button
          type="button"
          aria-label={docsExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
          aria-expanded={docsExpanded}
          tabIndex={onToggleDocs ? 0 : -1}
          disabled={!onToggleDocs}
          className={cn(
            'flex size-3.5 shrink-0 items-center justify-center rounded text-muted-foreground transition-transform motion-reduce:transition-none',
            !onToggleDocs && 'invisible',
            docsExpanded && 'rotate-90',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggleDocs?.()
          }}
        >
          <ChevronRight className="size-3" />
        </button>
        <span data-drag-icon className="flex shrink-0">
          <LocationSwatch location={project} size={16} />
        </span>
        <button
          type="button"
          className="min-w-0 flex-1 cursor-[inherit] truncate text-left"
          onClick={(e) => {
            e.stopPropagation()
            void open()
          }}
        >
          {project.name}
        </button>
        {openCount > 0 && (
          <span className={cn('group-hover/row:hidden group-focus-within/row:hidden', menuOpen && 'hidden')}>
            <RowCount>{openCount}</RowCount>
          </span>
        )}
        {/* "+" and "⋯" replace the count on hover (design 22c / 23). */}
        <div
          className={cn(
            'ml-auto hidden shrink-0 items-center gap-0.5 group-hover/row:flex group-focus-within/row:flex [@media(hover:none)]:flex',
            menuOpen && 'flex',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label={tNav('spaceNav.newTaskIn', { name: project.name })}
            title={tNav('spaceNav.newTaskIn', { name: project.name })}
            onClick={onNewTask}
            className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            <Plus className="size-3.5" />
          </button>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('sidebar.project.optionsAria')}
                className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" className="w-52">
              <DropdownMenuItem onSelect={onNewTask}>
                <Plus className="mr-2 size-4" />
                {tNav('spaceNav.newTaskIn', { name: project.name })}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  navigate({
                    to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/settings',
                    params: { workspaceSlug, spaceKey, projectKey: pk },
                  })
                }
              >
                <Settings className="mr-2 size-4" />
                {t('sidebar.project.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  void navigator.clipboard.writeText(window.location.origin + projectPath)
                  toast.success(t('sidebar.toast.linkCopied'))
                }}
              >
                <Link2 className="mr-2 size-4" />
                {t('sidebar.project.copyLink')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShowDetails(true)}>
                <Info className="mr-2 size-4" />
                {t('sidebar.project.details')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setShowDeleteConfirm(true)}>
                <Trash2 className="mr-2 size-4" />
                {t('sidebar.project.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ProjectDetailsDrawer project={project} open={showDetails} onOpenChange={setShowDetails} />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.project.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {openTaskCount > 0
                ? t('sidebar.project.deleteBlockedDescription', { count: openTaskCount, name: project.name })
                : t('sidebar.project.deleteDescription', { name: project.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deletePlanLoading || openTaskCount > 0}
              onClick={async () => {
                if (deletePlanLoading || openTaskCount > 0) {
                  toast.error(t('sidebar.toast.projectDeleteBlocked'))
                  return
                }
                try {
                  await deleteProjectTree()
                  toast.success(t('sidebar.toast.projectDeleted'))
                } catch {
                  toast.error(t('sidebar.toast.projectDeleteFailed'))
                }
              }}
            >
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
